import { Loading } from '@/components';
import { useCheckRemoveNotExistUserIdOrNotExistOrganizationId } from '@/features/project/_hooks/form-control';
import {
  useProject,
  useUpdateProject,
} from '@/features/project/_hooks/project';
import { ProjectForm } from '@/features/project/projects/forms/_components/ProjectForm';
import type {
  KsFormSchema,
  ProjectFormSchema,
  RsFormSchema,
  SsFormSchema,
} from '@/features/project/projects/forms/_model/project-form';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { boolToString } from '@/utils';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { z } from 'zod';
import { DiscriminatedInspectionEvaluationModel } from '../../_models/project';
import { useUpsertFormInitialValue } from '../_hooks/initial-value';

export const UpdateProjectPage: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { openMessage } = useMessage();
  const { id } = useId();
  const { data: projectData } = useProject(id);
  const { mutateAsync: updateProject } = useUpdateProject();
  const {
    openUpdateConfirmModal,
    onCancelModal,
    checkRemoveNotExistUserIdOrNotExistOrganizationId,
  } = useCheckRemoveNotExistUserIdOrNotExistOrganizationId();

  const {
    organizationIds,
    nestedRelatedOrganizationIds,
    nestedOtherResponsibleOrganizationIds,
    independentAdministrativeAgencyIds,
    fundAdditionalAttributes,
    policiesForProject,
    expenditureBudgetPurpose,
    isInitialDataFetchedLoading,
    initialSnapshot,
  } = useUpsertFormInitialValue(projectData);

  // NOTE: 2025年度のKSレイアウト変更により、KSでは基本情報タブに InspectionEvaluation の情報を表示するようになった (PRZ-889)
  const parsedProject = DiscriminatedInspectionEvaluationModel.safeParse({
    ...projectData,
  });

  if (!projectData || isInitialDataFetchedLoading) {
    return <Loading />;
  }

  const onUpdateProject = async (values: z.infer<typeof ProjectFormSchema>) => {
    const res = await updateProject({
      id,
      ...values,
    });

    if (res.baseOrganizationChanged) {
      openMessage('success', t('change-organization-of-project-completed'));
    }
    const isUpdateMajorExpenditureBudgetPurpose =
      projectData.majorExpenditureBudgetPurposeId !==
      res.majorExpenditureBudgetPurposeId;

    isUpdateMajorExpenditureBudgetPurpose
      ? openMessage(
          'warning',
          t('warning-message-change-major-expenditure-budget-update-completed'),
        )
      : openMessage('success', t('message-update-completed'));

    navigate(location.pathname.split('/').slice(0, -1).join('/'));
  };

  return (
    <>
      <ProjectForm
        type="edit"
        ignoredIdsForRelatedProject={[id]}
        openUpdateConfirmModal={openUpdateConfirmModal}
        onCancelModal={onCancelModal}
        onUpdateProject={onUpdateProject}
        initialValues={
          projectData.sheetType === 'RS'
            ? {
                sheetType: 'RS',
                data: {
                  // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                  ...(projectData as unknown as z.infer<
                    typeof RsFormSchema
                  >['data']),
                  skipAdministrativeReformTeamEvaluation:
                    projectData.skipAdministrativeReformTeamEvaluation,
                  fiscalYear: dayjs().year(projectData.fiscalYear),
                  period: [
                    projectData.startYear
                      ? dayjs().year(projectData.startYear)
                      : undefined,
                    projectData.endYear
                      ? dayjs().year(projectData.endYear)
                      : undefined,
                  ],
                  organizationIds,
                  nestedRelatedOrganizationIds,
                  policiesForProject,
                  mainCostIds: projectData.mainCosts.map(
                    (mainCost) => mainCost.id,
                  ),
                  relatedProjects: projectData.relatedProjects.map(
                    (relatedProject) => ({
                      id: relatedProject.id,
                      relationType: relatedProject.relationType,
                    }),
                  ),
                  nestedOtherResponsibleOrganizationIds,
                  majorExpenditureBudgetPurposeIds: [
                    expenditureBudgetPurpose?.accountingId,
                    expenditureBudgetPurpose?.jurisdictionId,
                    expenditureBudgetPurpose?.accountingType.type === 'general'
                      ? expenditureBudgetPurpose.organizationId
                      : expenditureBudgetPurpose?.accountTitleId,
                    expenditureBudgetPurpose?.expenditurePurpose.upperId,
                    expenditureBudgetPurpose?.id,
                  ].flatMap((v) => v ?? []),
                  fiscalYearOfSnapshot: initialSnapshot,
                },
              }
            : projectData.sheetType === 'SS'
              ? {
                  sheetType: 'SS',
                  data: {
                    // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                    ...(projectData as unknown as z.infer<
                      typeof SsFormSchema
                    >['data']),
                    fiscalYear: dayjs().year(projectData.fiscalYear),
                    period: [
                      projectData.startYear
                        ? dayjs().year(projectData.startYear)
                        : undefined,
                      projectData.endYear
                        ? dayjs().year(projectData.endYear)
                        : undefined,
                    ],
                    organizationIds,
                    nestedRelatedOrganizationIds,
                    independentAdministrativeAgencyIds,
                    mainCostIds: projectData.mainCosts.map(
                      (mainCost) => mainCost.id,
                    ),
                    relatedProjects: projectData.relatedProjects.map(
                      (relatedProject) => ({
                        id: relatedProject.id,
                        relationType: relatedProject.relationType,
                      }),
                    ),
                    nestedOtherResponsibleOrganizationIds,
                    fiscalYearOfSnapshot: initialSnapshot,
                  },
                }
              : {
                  sheetType: 'KS',
                  data: {
                    // FIXME: zod parseするか、propertyを一個ずつ渡して型エラーをなくしてください
                    ...(projectData as unknown as z.infer<
                      typeof KsFormSchema
                    >['data']),
                    fundAdditionalAttributes: fundAdditionalAttributes
                      ? {
                          ...fundAdditionalAttributes,
                          scheduledEnd: {
                            ...fundAdditionalAttributes?.scheduledEnd,
                            endDate: fundAdditionalAttributes?.scheduledEnd
                              ?.endDate
                              ? dayjs(
                                  fundAdditionalAttributes.scheduledEnd.endDate,
                                )
                              : undefined,
                            endDateOfNewApplicationReceiving:
                              fundAdditionalAttributes?.scheduledEnd
                                ?.endDateOfNewApplicationReceiving
                                ? dayjs(
                                    fundAdditionalAttributes.scheduledEnd
                                      .endDateOfNewApplicationReceiving,
                                  )
                                : undefined,
                          },
                        }
                      : null,
                    fiscalYear: dayjs().year(projectData.fiscalYear),
                    period: [
                      projectData.startYear
                        ? dayjs().year(projectData.startYear)
                        : undefined,
                      projectData.endYear
                        ? dayjs().year(projectData.endYear)
                        : undefined,
                    ],
                    organizationIds,
                    nestedRelatedOrganizationIds,
                    relatedProjects: projectData.relatedProjects.map(
                      (relatedProject) => ({
                        id: relatedProject.id,
                        relationType: relatedProject.relationType,
                      }),
                    ),
                    nestedOtherResponsibleOrganizationIds,
                    inspectionEvaluation:
                      parsedProject.success &&
                      parsedProject.data.sheetType === 'KS'
                        ? {
                            ...parsedProject.data.inspectionEvaluation,
                            inspectionAndImprovementByAdministrativeJurisdiction:
                              {
                                ...parsedProject.data.inspectionEvaluation
                                  .inspectionAndImprovementByAdministrativeJurisdiction,
                              },
                            ownershipRatio: {
                              ...parsedProject.data.inspectionEvaluation
                                .ownershipRatio,
                              operationType:
                                parsedProject.data.inspectionEvaluation
                                  .ownershipRatio?.operationType ?? 'auto',
                            },
                            lowUsedFundInvestigation: {
                              ...parsedProject.data.inspectionEvaluation
                                .lowUsedFundInvestigation,
                              hasCeasedOperations: boolToString(
                                parsedProject.data.inspectionEvaluation
                                  .lowUsedFundInvestigation
                                  ?.hasCeasedOperations,
                              ),
                              hasNoResultRecent: boolToString(
                                parsedProject.data.inspectionEvaluation
                                  .lowUsedFundInvestigation?.hasNoResultRecent,
                              ),
                              hasFundThatLostPurpose: boolToString(
                                parsedProject.data.inspectionEvaluation
                                  .lowUsedFundInvestigation
                                  ?.hasFundThatLostPurpose,
                              ),
                              hasExceedOneSignificantly: boolToString(
                                parsedProject.data.inspectionEvaluation
                                  .lowUsedFundInvestigation
                                  ?.hasExceedOneSignificantly,
                              ),
                              hasDeemedUnlikelyToBeUsed: boolToString(
                                parsedProject.data.inspectionEvaluation
                                  .lowUsedFundInvestigation
                                  ?.hasDeemedUnlikelyToBeUsed,
                              ),
                            },
                          }
                        : undefined,
                    fiscalYearOfSnapshot: initialSnapshot,
                  },
                }
        }
        onSubmit={async (values) => {
          const { isRemovedNotExistValue } =
            await checkRemoveNotExistUserIdOrNotExistOrganizationId(values);
          if (!isRemovedNotExistValue) {
            await onUpdateProject(values);
          }
        }}
      />
    </>
  );
};
